/// <reference path="../types.d.ts" />
import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../core/net';
import { caseUtils, commonActions, commonEnums as enums, getCodeRequests, abortController, appServiceLines, cmxTypeguards, cmxArtifacts, commonEnums, dictionaries, uiActions, api as commonApi, documentUtils, HttpMethods, launchPageActions } from '@codametrix/ui-common';
import { dismissFeedback, showFeedbackError, showFeedbackWorking, fetchCaseDetailsLayoutConfiguration, setBackToWorklistPath } from './ui';
import { BROADCAST_ACTION_METADATA, USER_ACTION_METADATA } from './action-constants';
import { isNumber } from '../core/type-guards';
import { objectUtils } from '@codametrix/ui-common';
import { gql } from '@apollo/client';
import { getServiceDeskClient } from '../clients/service-desk.client';
import { docMetadataAvailable, getDocuments } from './case-documents.actions';
import { AmplifyCore } from '@codametrix/ui-common';
const { uiStatus } = commonEnums;
const { paginationUtils } = AmplifyCore;
const { statusColors, statusBGColors } = enums;
const { getCaseRun } = caseUtils;
const { Case } = commonActions;
const { amplifyFeedback } = uiActions;
const featureSelection = createAction(Case.FEATURE_SELECTION);
const featureActivation = createAction(Case.FEATURE_ACTIVATION);
const caseOrganizationContext = createAction(Case.ORGANIZATION_CONTEXT);
const clearAllFilters = createAction(Case.CLEAR_ALL_FILTERS);
const applyDefaultFilters = createAction(Case.APPLY_DEFAULT_FILTERS);
const showProcessingTimeline = createAction(Case.TOGGLE_TIMELINE);
const detailToggleArtifacts = createAction(Case.DETAIL_TOGGLE_ARTIFACTS);
const detailInitial = createAction(Case.DETAIL_INIT);
const listInitial = createAction(Case.LIST_INIT);
const caseSelected = createAction(Case.SELECTED);
const fetchInvoiceCodeDescriptions = createAction(Case.INVOICE_CODE_DESCRIPTIONS);
const setActiveNote = createAction(Case.SET_ACTIVE_NOTE);
const setActiveProcess = createAsyncThunk(Case.SET_ACTIVE_PROCESS_ID, async (params, { dispatch })=>{
    if (isNumber(params.caseRun.billing_group_id)) {
        dispatch(getBillingGroup({
            billingGroupId: params.caseRun.billing_group_id,
            serviceLine: params.serviceLine
        }));
    }
    dispatch(amplifyProcessInstance(params.caseRun));
});
const list = createAsyncThunk(Case.LIST, async (params, { dispatch })=>{
    const { sortablePageable, serviceLine } = params;
    const serviceLineConfig = appServiceLines.determineServiceLineConfig(serviceLine);
    dispatch(listInitial(sortablePageable));
    dispatch(showFeedbackWorking({
        message: `Loading cases...`,
        dismissable: false
    }));
    const signal = abortController.getSignal(Case.LIST);
    const searchParams = paginationUtils.searchParamsFactory(sortablePageable);
    let body = [];
    if (sortablePageable.filterableOptions.filters) {
        body = sortablePageable.filterableOptions.filters;
        dispatch(applyFilters(sortablePageable.filterableOptions.filters));
    } else {
        dispatch(applyFilters([]));
    }
    if (sortablePageable.filterableOptions.dateFilter) {
        const dateKey = sortablePageable.filterableOptions.dateFilter.key;
        body = body.filter((filter)=>{
            const isDateRangeClause = filter.key === dateKey && (filter.type === 'GREATER_THAN_EQUAL' || filter.type === 'LESS_THAN_EQUAL');
            return !isDateRangeClause;
        });
        body.push({
            key: sortablePageable.filterableOptions.dateFilter.key,
            terms: [
                sortablePageable.filterableOptions.dateFilter.from
            ],
            type: 'GREATER_THAN_EQUAL'
        });
        body.push({
            key: sortablePageable.filterableOptions.dateFilter.key,
            terms: [
                sortablePageable.filterableOptions.dateFilter.to
            ],
            type: 'LESS_THAN_EQUAL'
        });
    }
    dispatch(applyDateFilter(sortablePageable.filterableOptions.dateFilter));
    const caseListPageable = await api({
        endpoint: `${serviceLineConfig.path}/caseinstance/list/v2?${searchParams.toString()}`,
        init: {
            method: 'POST',
            signal
        },
        body: body
    });
    dispatch(dismissFeedback(true));
    return caseListPageable;
});
const getDictionary = createAsyncThunk(Case.DICTIONARY, async (params, { dispatch })=>{
    const dictionaryParams = new URLSearchParams([
        [
            'dictionaryName',
            params.categoryName
        ]
    ]);
    const values = await api({
        endpoint: `/dictionary/dictionaryvalues/v1?${dictionaryParams.toString()}`
    });
    let FieldDefinitions = [];
    if (values !== undefined) {
        values.forEach((criteria)=>{
            FieldDefinitions.push({
                label: undefined,
                key: criteria.abbreviation,
                helpText: criteria.abbreviation,
                required: false,
                type: 'checkbox'
            });
        });
    }
    return {
        fields: FieldDefinitions,
        label: 'Filter by ' + params.key
    };
});
const getCaseFilter = createAsyncThunk(Case.CASE_STATUS, async (params, { dispatch })=>{
    const serviceLineConfig = appServiceLines.determineServiceLineConfig(params.serviceLine);
    let values = await api({
        endpoint: `${serviceLineConfig.path}/casebuilder/filter/${params.categoryName.toString()}/v1`
    });
    // Temp fix - endpoint will be updated in CDMX-134
    values = [
        ...values,
        commonEnums.uiStatus.CLOSED_NON_CHARGEABLE
    ];
    let FieldDefinitions = [];
    if (values !== undefined) {
        values.forEach((status)=>{
            const colors = getColor(status);
            if (colors) {
                FieldDefinitions.push({
                    label: undefined,
                    key: status,
                    helpText: status,
                    required: false,
                    type: 'checkbox',
                    color: colors.color,
                    bgColor: colors.bgColor
                });
            } else {
                FieldDefinitions.push({
                    label: undefined,
                    key: status,
                    helpText: status,
                    required: false,
                    type: 'checkbox'
                });
            }
        });
    }
    return {
        fields: FieldDefinitions,
        label: 'Filter by ' + params.key
    };
});
const getColor = (status)=>{
    switch(status){
        case uiStatus.IN_PROGRESS:
            return {
                color: statusColors.IN_PROGRESS,
                bgColor: statusBGColors.IN_PROGRESS
            };
        case uiStatus.AUTOMATED:
            return {
                color: statusColors.AUTOMATED,
                bgColor: statusBGColors.AUTOMATED
            };
        case uiStatus.MANUAL:
            return {
                color: statusColors.MANUAL,
                bgColor: statusBGColors.MANUAL
            };
        case uiStatus.CANCELLED:
            return {
                color: statusColors.CANCEL,
                bgColor: statusBGColors.CANCEL
            };
        case uiStatus.CLOSED_NON_CHARGEABLE:
            return {
                color: statusColors.CANCEL,
                bgColor: statusBGColors.CANCEL
            };
        case uiStatus.IN_PROCESS:
            return {
                color: statusColors.IN_PROGRESS,
                bgColor: statusBGColors.IN_PROGRESS
            };
        case uiStatus.UPDATED_MANUAL:
            return {
                color: statusColors.UPDATED_MANUAL,
                bgColor: statusBGColors.UPDATED_MANUAL
            };
    }
    return undefined;
};
const applyFilters = createAsyncThunk(Case.FILTER, async (filters, { dispatch })=>{
    return filters;
});
const applyDateFilter = createAsyncThunk(Case.DATE_FILTER, async (filters, { dispatch })=>{
    return filters;
});
const getShowOrderNumber = createAsyncThunk(Case.SHOW_ORDER_NUMBER, async (params, { dispatch })=>{
    const { key, tenantId } = params;
    const searchParams = new URLSearchParams({
        key: key,
        tenantId: tenantId
    });
    try {
        const showOrderNumber = await api({
            endpoint: `/configuration/value/v1?${searchParams.toString()}`,
            init: {
                method: HttpMethods.GET
            }
        });
        return showOrderNumber[0];
    } catch (error) {
        console.log(error);
        throw error;
    }
});
const getBillingGroup = createAsyncThunk(Case.BILLING_GROUP, async (params, { dispatch })=>{
    const { billingGroupId, serviceLine } = params;
    const signal = abortController.getSignal(Case.BILLING_GROUP);
    const serviceLineConfig = appServiceLines.determineServiceLineConfig(serviceLine);
    const searchParams = new URLSearchParams({
        page: '0',
        pageSize: '25',
        sort: 'dateOfService,DESC'
    });
    searchParams.append('sort', 'id,DESC');
    let body = [];
    body.push({
        key: `billing_group_id`,
        terms: [
            `${billingGroupId}`
        ],
        type: 'EQ'
    });
    const billingGroupPageable = await api({
        endpoint: `${serviceLineConfig.path}/caseinstance/list/v2?${searchParams.toString()}`,
        init: {
            method: 'POST',
            signal
        },
        body: body
    });
    return billingGroupPageable;
});
const getSubmitterProcess = createAsyncThunk(Case.SUBMITTER_ACCESSION_SEARCH, async (params, { dispatch })=>{
    const signal = abortController.getSignal(Case.SUBMITTER_ACCESSION_SEARCH);
    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
        name: params.serviceLine
    });
    const searchParams = new URLSearchParams({
        page: '0',
        pageSize: '25',
        sort: 'dateOfService,DESC'
    });
    searchParams.append('sort', 'id,DESC');
    let body = [];
    body.push({
        key: `primary_clinical_identifier`,
        terms: [
            `${params.submitterAccession}`
        ],
        type: 'EQ'
    });
    const submitterPageable = await api({
        endpoint: `${serviceLineConfig.path}/caseinstance/list/${serviceLineConfig.caseListVersion}?${searchParams.toString()}`,
        init: {
            method: 'POST',
            signal
        },
        body: body
    });
    const caseId = submitterPageable.content.length ? submitterPageable.content[0].id : false;
    if (caseId === false) {
        throw new Error(`no case found for ${params.submitterAccession}`);
    }
    const caseParams = new URLSearchParams(serviceLineConfig.caseDetailParms);
    const expanded = await api({
        endpoint: `${serviceLineConfig.path}/caseinstance/${caseId}/details/${serviceLineConfig.caseDetailVersion}?${caseParams.toString()}`
    });
    const invoiceMode = getInvoiceMode(expanded);
    let descriptionRequests = formatDescriptionRequests(expanded);
    dispatch(fetchCodeRequests({
        caseDetail: expanded,
        descriptionRequests,
        invoiceMode
    }));
    return expanded;
});
const buildCodeRequests = (caseRun)=>{
    const artifacts = caseRun?.artifacts ?? [];
    return getCodeRequests(artifacts, caseRun);
};
const flattenCodeRequests = (codeRequests)=>{
    const flat = codeRequests.flat();
    objectUtils.uniqBy(flat, (code)=>`${code.code}-${code.effectiveDate}`);
    return flat;
};
const importToAmplify = createAsyncThunk(Case.IMPORT_TO_AMPLIFY, async (caseDetailAction, { dispatch })=>{
    const { caseRun, caseOrganizationContext } = caseDetailAction;
    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
        name: caseRun.service_line
    });
    const endpoint = `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${caseRun.process_instance_guid}/v1?tenantIdOverride=${caseOrganizationContext.tenantId}`;
    try {
        const caseImport = await api({
            endpoint,
            init: {
                method: 'POST'
            }
        });
        return caseImport;
    } catch (e) {
        dispatch(showFeedbackError({
            id: Date.now(),
            message: `Unable to import case into Amplify.`,
            dismissable: true
        }));
        throw e;
    }
});
const fetchCodeRequests = createAsyncThunk(Case.INVOICE_CODE_DESCRIPTIONS, async ({ caseDetail, descriptionRequests, invoiceMode }, { dispatch })=>{
    // even if there are no descriptionRequests, we still need to initialize
    // an organization context for a case.
    const primaryCase = getCaseRun(caseDetail);
    const tenantInfo = await api({
        endpoint: `/organization/by-name/${primaryCase.tenant_name ?? 'Henry Ford Health System'}/v1`
    });
    dispatch(caseOrganizationContext(tenantInfo));
    if (descriptionRequests.length) {
        try {
            const descriptions = await api({
                endpoint: `/dictionary/codes/description/v2?tenantId=${tenantInfo.parentTenantId}`,
                init: {
                    method: 'POST'
                },
                body: descriptionRequests
            });
            const normalizeDescriptions = dictionaries.normalizeDescriptions(descriptions);
            if (invoiceMode) {
                dispatch(fetchInvoiceCodeDescriptions(normalizeDescriptions));
            }
            return normalizeDescriptions;
        } catch (e) {
            console.log(e);
        }
    }
    return Promise.resolve({});
});
const navigateDetail = createAsyncThunk(Case.DETAIL_WILL_NAVIGATE, async (caseDetail, { dispatch, fulfillWithValue })=>{
    dispatch(caseSelected(caseDetail));
    const queryString = window.location.search.slice(1);
    const sl = appServiceLines.toServiceLineSlug(String(caseDetail.service_line ?? ''));
    const base = `/cmx/cases/${sl}/`;
    if (queryString) {
        history.push(`${base}${caseDetail.case_id}?${queryString}`);
    //@ts-ignore
    } else if (caseDetail.caserun_uid) {
        //@ts-ignore
        history.push(`${base}${caseDetail.caserun_uid}/`);
    } else {
        history.push(`${base}${caseDetail.case_id}/`);
    }
    return fulfillWithValue(caseDetail, BROADCAST_ACTION_METADATA);
}, {
    getPendingMeta: ()=>({
            ...BROADCAST_ACTION_METADATA
        })
});
const getDetail = createAsyncThunk(Case.DETAIL, async (payload, { dispatch, rejectWithValue, fulfillWithValue })=>{
    const userActionMeta = USER_ACTION_METADATA;
    try {
        const { caseId, serviceLine, tenantId, hasAmplifyCaseRunV2Access } = payload;
        const signal = abortController.getSignal(Case.DETAIL);
        const serviceLineConfig = appServiceLines.determineServiceLineConfig({
            name: serviceLine,
            caseIdentifier: caseId
        });
        dispatch(detailInitial(caseId));
        const caseParams = new URLSearchParams(serviceLineConfig.caseDetailParms);
        const expandedCaseDetail = await api({
            endpoint: `${serviceLineConfig.path}/caseinstance/${caseId}/details/${serviceLineConfig.caseDetailVersion}?${caseParams.toString()}`,
            init: {
                signal
            }
        });
        // we will always start with the primary process.
        const primaryCase = getCaseRun(expandedCaseDetail);
        // if in bedside pro, get the rtf document's html
        if (appServiceLines.isIPC(primaryCase.service_line)) {
            await dispatch(getDocumentsAsHtml(primaryCase));
        }
        // fetch code descriptions
        const invoiceMode = getInvoiceMode(expandedCaseDetail);
        let descriptionRequests = formatDescriptionRequests(expandedCaseDetail);
        dispatch(fetchCodeRequests({
            caseDetail: expandedCaseDetail,
            descriptionRequests,
            invoiceMode
        }));
        // check for additional exams within a billing group
        if (isNumber(primaryCase.billing_group_id)) {
            dispatch(getBillingGroup({
                billingGroupId: primaryCase.billing_group_id,
                serviceLine: {
                    name: serviceLine,
                    caseIdentifier: caseId
                }
            }));
        }
        // if there's a submitter process fetch contents
        // https://codametrix.atlassian.net/browse/XAPP-1560
        if (primaryCase.submitter_process === false && primaryCase.submitted_primary_clinical_identifier?.number) {
            const accesionNumber = primaryCase.submitted_primary_clinical_identifier?.number;
            dispatch(getSubmitterProcess({
                submitterAccession: `${accesionNumber}`,
                serviceLine
            }));
        }
        dispatch(amplifyProcessInstance(primaryCase));
        // Load case-details-layout configuration on case open
        if (tenantId) {
            dispatch(// @ts-ignore
            fetchCaseDetailsLayoutConfiguration({
                tenantId,
                serviceLine: primaryCase.service_line,
                hasAmplifyCaseRunV2Access: hasAmplifyCaseRunV2Access ?? false
            }));
        }
        return fulfillWithValue(expandedCaseDetail, userActionMeta);
    } catch (e) {
        return rejectWithValue(e, userActionMeta);
    }
}, {
    getPendingMeta: ()=>({
            ...USER_ACTION_METADATA
        })
});
export const getInvoiceMode = (expandedCaseDetail)=>{
    if (cmxTypeguards.isCaseV2(expandedCaseDetail)) {
        return true;
    }
    const primaryCase = getCaseRun(expandedCaseDetail);
    return primaryCase.invoices && primaryCase.invoices.length > 0 || false;
};
export const formatDescriptionRequests = (expandedCaseDetail)=>{
    const invoiceMode = getInvoiceMode(expandedCaseDetail);
    const codeRequests = expandedCaseDetail.caseRuns.map(buildCodeRequests);
    let descriptionRequests = flattenCodeRequests(codeRequests);
    if (invoiceMode) {
        descriptionRequests = [
            ...descriptionRequests,
            ...cmxArtifacts.getInvoiceCodeRequests(expandedCaseDetail.caseRuns)
        ];
    }
    return descriptionRequests;
};
const amplifyProcessInstance = createAsyncThunk(commonEnums.ProcessInstanceActions.DASHBOARD_INVOICE_AVAILABLE, async (caseRun, { dispatch })=>{
    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
        name: caseRun.service_line
    });
    const processInstance = await api({
        endpoint: `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${caseRun.process_instance_guid}/v1`
    });
    // For a miss, the response from the coding-api is empty but
    // 200.  We need to do our own verification on the response here.
    return processInstance;
});
const createForm = (org)=>{
    if (org === undefined) {
        return;
    }
    const piece = {
        label: org.organizationName,
        key: org.organizationCode,
        helpText: org.organizationName,
        required: false,
        level: org.parentLevel
    };
    var children = undefined;
    if (org.parentLevel !== 2) {
        children = org.children?.map((child)=>{
            return createForm(child);
        });
    }
    const sortedChildren = children?.sort((first, second)=>{
        if (first && second && first.helpText !== undefined && second.helpText !== undefined) {
            var firstText = first.helpText.toUpperCase();
            var secondText = second.helpText.toUpperCase();
            return firstText < secondText ? -1 : firstText > secondText ? 1 : 0;
        }
        return 0;
    });
    if (sortedChildren !== undefined && sortedChildren.length > 0) {
        piece.children = sortedChildren;
    }
    return piece;
};
const getTenantFilter = createAsyncThunk(Case.TENANT, async (id)=>{
    const endpoint = isNaN(id) ? `/organization/tenant/v1?tenantId=${id}` : `/organization/id/v1?organizationId=${id}`;
    const orgOpts = {
        endpoint: endpoint
    };
    const organization = await api(orgOpts);
    if (organization.parentLevel === 0) {
        let form = createForm(organization);
        const children = form?.children;
        if (children !== undefined) {
            return children;
        }
    }
    let form = createForm(organization);
    if (form === undefined) {
        form = {
            key: 'invalid'
        };
    }
    return [
        form
    ];
});
const launchCodeBenchInAmplify = (route, launchConfig, dispatch)=>{
    const sl = appServiceLines.toServiceLineSlug(appServiceLines.normalizeServiceLineName(String(launchConfig.serviceLine ?? '')));
    dispatch(launchPageActions.navigationIntent({
        intent: 'CASE_DETAIL',
        url: `/cmx/${route}/${sl}/case/${launchConfig.caseId}/case-run/${launchConfig.processId}${launchConfig.hasAmplifyCaseRunV2Access ? '/v2' : ''}/`,
        state: {
            worklistContext: launchConfig.worklistContext,
            servicedeskMode: launchConfig.servicedeskMode,
            workitemId: launchConfig.workitemId,
            sessionId: launchConfig.sessionId,
            qaCollectionUuid: launchConfig.qaCollectionUuid,
            launchConfigServiceLine: launchConfig.serviceLine
        }
    }));
};
const launchGradeBenchInAmplify = (route, launchConfig, dispatch)=>{
    //CQA launches with the case_id of the workitem (assigned to workitemId in launchConfig)
    // Construct backToWorklistPath with query parameters to preserve selected project
    let backPath = `${route}/`;
    if (launchConfig.worklistContext?.selectedProjectId) {
        const params = new URLSearchParams();
        params.set('projectId', launchConfig.worklistContext.selectedProjectId);
        if (launchConfig.worklistContext?.selectedStatus) {
            params.set('status', launchConfig.worklistContext.selectedStatus);
        }
        backPath += `?${params.toString()}`;
    }
    // Set the backToWorklistPath with query params for "Back to Worklist" navigation
    dispatch(setBackToWorklistPath({
        path: backPath
    }));
    dispatch(launchPageActions.navigationIntent({
        intent: 'CASE_DETAIL',
        url: `/cmx/${route}/workItem/${launchConfig.workitemId}`,
        state: {
            worklistContext: launchConfig.worklistContext,
            servicedeskMode: launchConfig.servicedeskMode,
            workitemId: launchConfig.workitemId,
            sessionId: launchConfig.sessionId,
            qaCollectionUuid: launchConfig.qaCollectionUuid,
            launchConfigServiceLine: launchConfig.serviceLine
        }
    }));
};
/**
 * Asynchronously launches a work item in Amplify based on the provided configuration.
 *
 * @param {CMxCommonApp.LaunchWorkItemConfig} launchConfig - The configuration object for launching the work item,
 * containing details such as the service line, case ID, process ID, and flags indicating the type of work item.
 * Launch configs come from the Service-Desk which is a different repo than ui-omnibus
 */ const launchWorkitemInAmplify = createAsyncThunk(Case.LAUNCH_WORKITEM_IN_AMPLIFY, async (launchConfig, { dispatch })=>{
    if (launchConfig.isCodingQuality) {
        launchCodeBenchInAmplify('coding-quality', launchConfig, dispatch);
    } else if (launchConfig.isQualityAssessment) {
        launchGradeBenchInAmplify('quality-assessment', launchConfig, dispatch);
    } else {
        launchCodeBenchInAmplify('worklist', launchConfig, dispatch);
    }
});
/**
 * Action to fetch workitem details
 */ const getWorkItemDetails = createAsyncThunk(Case.FETCH_WORKITEM_DETAILS, async (params, { dispatch })=>{
    try {
        const xcaliberClient = getServiceDeskClient();
        xcaliberClient.initApolloClient();
        const QUERY_WORK_ITEMS_MUTATION = gql`
      mutation SearchWorkItems($request: SearchWorkItemRequest!) {
        SearchWorkItems(request: $request) {
          data
          total
        }
      }
    `;
        const response = await xcaliberClient.apolloClient.mutate({
            mutation: QUERY_WORK_ITEMS_MUTATION,
            variables: {
                request: {
                    combinator: 'AND',
                    filters: [
                        {
                            key: 'workitem.cmx_case_uid',
                            operation: 'eq',
                            value: params.case_uid
                        },
                        {
                            key: 'workitem.cmx_process_id',
                            operation: 'eq',
                            value: params.caserun_uid
                        }
                    ],
                    pagination: {},
                    groupByEncounter: true
                }
            }
        });
        const owner = response?.data?.SearchWorkItems?.data?.[0]?.data?.owner;
        const assignee = response?.data?.SearchWorkItems?.data?.[0]?.data?.assignee;
        const aging_days = response?.data?.SearchWorkItems?.data?.[0]?.data?.aging_days;
        const codable_days = response?.data?.SearchWorkItems?.data?.[0]?.data?.codable_days;
        const late_case_update = response?.data?.SearchWorkItems?.data?.[0]?.data?.late_case_update;
        const dismissed = response?.data?.SearchWorkItems?.data?.[0]?.data?.dismissed;
        const work_item_case_id = response?.data?.SearchWorkItems?.data?.[0]?.data?.case_id;
        const workItem = {
            owner,
            assignee,
            aging_days,
            codable_days,
            late_case_update,
            dismissed,
            work_item_case_id
        };
        return workItem;
    } catch (e) {
        console.log('There was an error fetching workitem details', e);
    }
    return {
        owner: '',
        assignee: '',
        aging_days: '',
        codable_days: ''
    };
});
/**
 * Action to update workitem dismissed status
 */ const updateWorkItemDismissed = createAsyncThunk(Case.UPDATE_WORKITEM_DISMISSED, async (params, { dispatch })=>{
    try {
        const xcaliberClient = getServiceDeskClient();
        xcaliberClient.initApolloClient();
        const UPDATE_WORK_ITEM_MUTATION = gql`
      mutation UpdateWorkItem($id: String!, $workItem: WorkItemsInput!) {
        UpdateWorkItem(id: $id, workItem: $workItem) {
          id
          status
          data
        }
      }
    `;
        const response = await xcaliberClient.apolloClient.mutate({
            mutation: UPDATE_WORK_ITEM_MUTATION,
            variables: {
                id: params.workItemId,
                workItem: {
                    dismissed: params.dismissed
                }
            }
        });
        return response?.data?.UpdateWorkItem;
    } catch (e) {
        console.log('There was an error updating workitem dismissed status', e);
    }
});
const getPrevOrNextCase = createAsyncThunk(Case.GET_PREV_OR_NEXT_CASE, async (cursorAPIPayload, { dispatch })=>{
    try {
        const { cursorAPIContext, operation, hasAmplifyCaseRunV2Access } = cursorAPIPayload;
        const worklistQuery = cursorAPIContext.worklistContext?.query;
        if (worklistQuery == null) {
            dispatch(amplifyFeedback({
                message: 'There was an issue fetching the next case.',
                meta: commonEnums.FeedbackMeta.ERROR,
                dismissable: true
            }));
            return;
        }
        const xcaliberClient = getServiceDeskClient();
        xcaliberClient.initApolloClient();
        const getPrevOrNextCase = gql`
        mutation GetPrevOrNextCase($cursorPayload: CursorPayload!) {
          GetPrevOrNextCase(cursorPayload: $cursorPayload) {
            case_id
            cmx_process_id
            cmx_case_uid
            service
            session_id
            key
            status
            status_msg
            qa_collection_id
          }
        }
      `;
        const response = await xcaliberClient.apolloClient.mutate({
            mutation: getPrevOrNextCase,
            variables: {
                cursorPayload: {
                    case_id: cursorAPIContext.workitemId,
                    query: {
                        combinator: worklistQuery.combinator ?? 'AND',
                        filters: worklistQuery.filters,
                        pagination: worklistQuery.pagination,
                        groupByEncounter: worklistQuery.groupByEncounter,
                        groupByCQAWorkItemId: worklistQuery.groupByCQAWorkItemId,
                        cqaNavigationOneRowPerGroup: worklistQuery.groupByCQAWorkItemId === true
                    },
                    operation: operation,
                    session_id: cursorAPIContext.sessionId ? cursorAPIContext.sessionId : null,
                    wl_grouping_id: cursorAPIContext.wl_grouping_id ?? null
                }
            }
        });
        const result = response?.data?.GetPrevOrNextCase;
        let feedbackFlag = false;
        if (result.status === commonEnums.ResponseStatus.SUCCESS) {
            if (result?.case_id) {
                const launchConfig = {
                    serviceLine: result?.service,
                    caseId: result?.cmx_case_uid,
                    processId: result?.cmx_process_id,
                    workitemId: result?.case_id,
                    servicedeskMode: cursorAPIContext.servicedeskMode,
                    worklistContext: cursorAPIContext.worklistContext,
                    sessionId: result?.session_id,
                    isCodingQuality: cursorAPIContext.worklistContext?.isCodingQuality,
                    isQualityAssessment: cursorAPIContext.worklistContext?.isQualityAssessment,
                    hasAmplifyCaseRunV2Access: hasAmplifyCaseRunV2Access,
                    qaCollectionUuid: result?.qa_collection_id
                };
                dispatch(launchWorkitemInAmplify(launchConfig));
            } else {
                if (result?.case_id === null) {
                    let path = '/cmx/worklist/';
                    if (cursorAPIContext.worklistContext?.isCodingQuality) {
                        path = '/cmx/coding-quality/';
                    } else if (cursorAPIContext.worklistContext?.isQualityAssessment) {
                        path = '/cmx/quality-assessment/';
                        // Add query parameters to preserve selected project
                        if (cursorAPIContext.worklistContext?.selectedProjectId) {
                            const params = new URLSearchParams();
                            params.set('projectId', cursorAPIContext.worklistContext.selectedProjectId);
                            if (cursorAPIContext.worklistContext?.selectedStatus) {
                                params.set('status', cursorAPIContext.worklistContext.selectedStatus);
                            }
                            path += `?${params.toString()}`;
                        }
                    }
                    dispatch(launchPageActions.navigationIntent({
                        intent: 'CASE_DETAIL',
                        url: path
                    }));
                } else {
                    feedbackFlag = true;
                }
            }
        } else {
            feedbackFlag = true;
        }
        if (feedbackFlag) {
            dispatch(amplifyFeedback({
                message: 'There was an issue fetching the next case.',
                meta: commonEnums.FeedbackMeta.ERROR,
                dismissable: true
            }));
        }
    } catch (error) {
        console.error('Error in getPrevOrNextCase', error);
        dispatch(amplifyFeedback({
            message: 'There was an issue fetching the next case.',
            meta: commonEnums.FeedbackMeta.ERROR,
            dismissable: true
        }));
    }
});
const getDocumentsAsHtml = createAsyncThunk('GET_HTML', async (caseRun, { dispatch })=>{
    const { tenant_name, child_case_list } = caseRun;
    const tenantInfo = await api({
        endpoint: `/organization/by-name/${tenant_name}/v1`
    });
    let docs = await getDocuments(caseRun, tenantInfo);
    let htmlNotes = [];
    let caseRuns = [
        caseRun
    ];
    for (const child of child_case_list ?? []){
        if (child.case_uid !== caseRun.case_uid) {
            const signal = abortController.getSignal(commonActions.Case.DETAIL);
            const serviceLineConfig = appServiceLines.determineServiceLineConfig({
                name: caseRun.service_line
            });
            const caseParams = new URLSearchParams(serviceLineConfig.caseDetailParms);
            const expandedCaseDetail = await api({
                endpoint: `${serviceLineConfig.path}/caseinstance/${child.case_uid}/details/${serviceLineConfig.caseDetailVersion}?${caseParams.toString()}`,
                init: {
                    signal
                }
            });
            caseRuns = caseRuns.concat(expandedCaseDetail?.caseRuns ?? []);
        }
    }
    let newDocs = [];
    const allowedDocs = new Set([
        'application/pdf',
        'text/html',
        'text/plain'
    ]);
    for (let doc of docs){
        const { id, mimeType } = doc;
        if (!id) {
            console.warn(`no id for document`, doc);
            continue;
        }
        if (allowedDocs.has(mimeType)) {
            // caseruns are an amalgam of all case runs associated with the case
            // so we need to find the caseRun that should be looked up in order
            // with this document
            caseRuns.sort((a, b)=>{
                // if either of the docs a parent, but not both, thenn the one with the parent should be first
                if (!a.parent_case || !b.parent_case) {
                    if (a.parent_case) {
                        return 1;
                    }
                    if (b.parent_case) {
                        return -1;
                    }
                }
                if (a.case_created_datetime === null || b.case_created_datetime === null) return 0;
                // fallback to the lastest created date
                return a.case_created_datetime > b.case_created_datetime ? 1 : -1;
            });
            const caseRun = caseRuns?.find((caseRun)=>{
                return caseRun.notes?.find((note)=>{
                    //@ts-ignore
                    if (note.unique_document_number === doc.uniqueDocumentNumber) return true;
                    return caseRun.caserun_uid === doc.businessKey;
                });
            });
            const notes = caseRun?.notes;
            const caseRunNote = notes?.find((note)=>{
                return note.unique_document_number === doc.uniqueDocumentNumber;
            });
            try {
                const endpoint = `/documents/download/${id}${mimeType === 'application/pdf' ? '/encoded' : ''}/v1`;
                const content = await commonApi.request(endpoint);
                const text = await content.text();
                const htmlNote = documentUtils.decoratedNoteFactory({
                    description: caseRunNote?.description ?? doc.documentDescription,
                    content: text,
                    filename: caseRunNote?.note_specialty_designation ?? 'Document',
                    type: doc.documentType,
                    document_id: doc.documentId,
                    missedConversion: doc.missedConversion,
                    id,
                    filetype: mimeType,
                    primary: caseRunNote?.primary
                });
                htmlNotes.push(htmlNote);
                newDocs.push({
                    ...doc,
                    documentName: caseRunNote?.note_specialty_designation ?? 'Document'
                });
            } catch (e) {
                console.log('Cases: error fetching document', doc, e);
            }
        }
    }
    docs = newDocs.filter((doc)=>doc.documentId !== null);
    htmlNotes.sort(documentUtils.compareDecoratedNotesForNav);
    const docById = new Map(docs.map((d)=>[
            d.documentId,
            d
        ]));
    const orderedDocs = htmlNotes.map((n)=>docById.get(n.document_id)).filter((d)=>d != null);
    const seen = new Set(orderedDocs.map((d)=>d.documentId));
    for (const d of docs){
        if (!seen.has(d.documentId)) {
            orderedDocs.push(d);
        }
    }
    docs = orderedDocs;
    dispatch(docMetadataAvailable(docs));
    return htmlNotes;
});
export { importToAmplify, caseOrganizationContext, amplifyProcessInstance, list, getBillingGroup, navigateDetail, getDetail, getDictionary, getSubmitterProcess, applyFilters, applyDateFilter, fetchCodeRequests, fetchInvoiceCodeDescriptions, featureSelection, featureActivation, getCaseFilter, getTenantFilter, detailToggleArtifacts, clearAllFilters, applyDefaultFilters, showProcessingTimeline, setActiveProcess, launchWorkitemInAmplify, getPrevOrNextCase, getDocumentsAsHtml, setActiveNote, getShowOrderNumber, getWorkItemDetails, updateWorkItemDismissed };
